import { inject, Injectable, signal } from '@angular/core';
import {
  AdminPaymentsService,
  GetEventRevenueMetricResponseDto,
  GetRevenueKpiStatisticsResponse,
  GetRevenueTrendsResponse,
} from '@smatchy/sdk';

export interface RevenueFilters {
  startDate?: string;
  endDate?: string;
  eventId?: string;
  organizerId?: string;
  status?: string;
  sport?: string;
  page?: number;
  limit?: number;
  sortBy?: 'eventTitle' | 'organizerName' | 'ticketPrice' | 'eventRevenue' | 'refunds' | 'finalEventRevenue' | 'organizerRevenue' | 'smatchyRevenue' | 'status' | 'revenue' | 'ticketsSold' | 'eventDate';
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

@Injectable({ providedIn: 'root' })
export class RevenueStoreService {
  #revenueApi = inject(AdminPaymentsService);
  loading = signal(false);
  kpiStats = signal<GetRevenueKpiStatisticsResponse | null>(null);
  trends = signal<GetRevenueTrendsResponse | null>(null);
  eventMetrics = signal<GetEventRevenueMetricResponseDto[]>([]);
  eventMetricsTotal = signal(0);
  eventMetricsPage = signal(1);
  eventMetricsLimit = signal(25);

  private handleError(error: unknown, message: string): void {
    this.loading.set(false);
    console.error(message, error);
  }

  getRevenueKpiStatistics(filters?: {
    startDate?: string;
    endDate?: string;
    previousPeriodStart?: string;
    previousPeriodEnd?: string;
  }) {
    this.loading.set(true);
    return this.#revenueApi.getRevenueKpiStatistics(filters || {}).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.error) {
          this.handleError(res.error, 'Failed to load KPI statistics:');
          this.kpiStats.set(null);
        } else {
          this.kpiStats.set(res.value || null);
        }
      },
      error: (err) => {
        this.handleError(err, 'Failed to load KPI statistics:');
        this.kpiStats.set(null);
      },
    });
  }

  getRevenueTrends(filters?: {
    startDate?: string;
    endDate?: string;
    groupBy?: 'month';
  }) {
    this.loading.set(true);
    return this.#revenueApi.getRevenueTrends(filters || {}).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.error) {
          this.handleError(res.error, 'Failed to load revenue trends:');
          this.trends.set(null);
        } else {
          this.trends.set(res.value || null);
        }
      },
      error: (err) => {
        this.handleError(err, 'Failed to load revenue trends:');
        this.trends.set(null);
      },
    });
  }

  getEventRevenueMetrics(filters?: RevenueFilters) {
    this.loading.set(true);
    return this.#revenueApi.getEventRevenueMetrics(filters || {}).subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.error) {
          this.handleError(res.error, 'Failed to load event metrics:');
          this.eventMetrics.set([]);
          this.eventMetricsTotal.set(0);
        } else {
          const value = res.value;
          if (value) {            
            this.eventMetrics.set(value.events);
            this.eventMetricsTotal.set(value.total || 0);
            this.eventMetricsPage.set(value.page || 1);
            this.eventMetricsLimit.set(value.limit || 25);
          }
        }
      },
      error: (err) => {
        this.handleError(err, 'Failed to load event metrics:');
        this.eventMetrics.set([]);
        this.eventMetricsTotal.set(0);
      },
    });
  }
}

